import type { Database } from '../types/database'

type VisionBoard = Database['public']['Tables']['vision_boards']['Row']

export const EMPTY_BOARD_TITLE = 'Untitled'

const DATA_IMAGE_PREFIX = 'data:image/'
const DATA_SVG_IMAGE_PREFIX = 'data:image/svg+xml'
const ALLOWED_THUMBNAIL_PROTOCOLS = new Set(['http:', 'https:', 'blob:'])

export const isRenderableBoardGalleryImageSrc = (
  value: string | null | undefined
): boolean => {
  const src = value?.trim()
  if (!src) return false

  if (src.startsWith(DATA_SVG_IMAGE_PREFIX)) {
    return false
  }

  if (src.startsWith(DATA_IMAGE_PREFIX)) {
    return src.length > DATA_IMAGE_PREFIX.length
  }

  try {
    const parsedUrl = new URL(src)
    return ALLOWED_THUMBNAIL_PROTOCOLS.has(parsedUrl.protocol)
  } catch {
    return false
  }
}

export const getBoardDisplayTitle = (
  title: string | null | undefined
): string => {
  const trimmedTitle = title?.trim()
  return trimmedTitle ? trimmedTitle : EMPTY_BOARD_TITLE
}

export const hasRenderableBoardThumbnail = (
  board: Pick<VisionBoard, 'thumbnail_url'>
): boolean => {
  return isRenderableBoardGalleryImageSrc(board.thumbnail_url)
}

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  return value as Record<string, unknown>
}

const parseCanvasData = (value: unknown): Record<string, unknown> | null => {
  if (typeof value === 'string') {
    try {
      return asRecord(JSON.parse(value))
    } catch {
      return null
    }
  }

  return asRecord(value)
}

export const getBoardImageCount = (
  board: Pick<VisionBoard, 'canvas_data'>
): number => {
  const canvasData = parseCanvasData(board.canvas_data)
  const rawElements = canvasData?.elements

  if (!Array.isArray(rawElements)) {
    return 0
  }

  return rawElements.filter((rawElement) => {
    const element = asRecord(rawElement)
    if (!element || element.type !== 'image') return false

    return isRenderableBoardGalleryImageSrc(
      typeof element.src === 'string' ? element.src : null
    )
  }).length
}

const getRawBoardImageCount = (
  board: Pick<VisionBoard, 'canvas_data'>
): number => {
  const canvasData = parseCanvasData(board.canvas_data)
  const rawElements = canvasData?.elements

  if (!Array.isArray(rawElements)) {
    return 0
  }

  return rawElements.filter((rawElement) => {
    const element = asRecord(rawElement)
    if (!element || element.type !== 'image') return false

    const src = typeof element.src === 'string' ? element.src.trim() : ''
    return src.length > 0
  }).length
}

export const getBoardPhotoCount = (
  board: Pick<VisionBoard, 'canvas_data'>
): number => {
  const safeImageCount = getBoardImageCount(board)
  return safeImageCount > 0 ? safeImageCount : getRawBoardImageCount(board)
}

export const isMobileBoardCanvas = (
  board: Pick<VisionBoard, 'canvas_data'>
): boolean => {
  const canvasData = parseCanvasData(board.canvas_data)
  return canvasData?.version === 'mobile-board-v1'
}

export const shouldRenderMobileBoardStatePreview = (
  board: Pick<VisionBoard, 'canvas_data'>
): boolean => {
  return isMobileBoardCanvas(board) && getBoardImageCount(board) > 0
}

export const hasBoardGalleryPreview = (
  board: Pick<VisionBoard, 'canvas_data' | 'thumbnail_url'>
): boolean => {
  const imageCount = getBoardImageCount(board)
  if (imageCount > 0) return true

  if (getRawBoardImageCount(board) > 0) {
    return hasRenderableBoardThumbnail(board)
  }

  return (
    !parseCanvasData(board.canvas_data) && hasRenderableBoardThumbnail(board)
  )
}
